import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './store';
import { isDefinitiveAuthFailure, useMe } from './useMe';
import { homePathForRoles } from './role-home';

/**
 * Guards for authenticated / guest-only routes.
 *
 * Auth state has two sources of truth that can disagree after a session
 * expires: the persisted zustand snapshot in localStorage, and the live
 * result of `GET /api/v1/me`. `/me` is authoritative — if it says 401 we
 * must not keep rendering a "signed in" shell just because localStorage
 * still remembers a user. Failing to reconcile the two caused a redirect
 * loop between /dashboard (AuthGuard trusts stored user) and /login
 * (GuestGuard also trusts stored user and bounces back).
 *
 * Only a 401 counts as "not signed in". This guard used to treat *any* /me
 * failure as one, so a single 500 or dropped connection — easy to hit when the
 * dashboard opens a dozen calls at once against a single-threaded dev server —
 * signed the user straight back out a second after logging in. A server that
 * cannot be reached is a server problem to show, not a reason to throw away a
 * good session.
 */

export function AuthGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const storedUser = useAuth((s) => s.user);
  const clear = useAuth((s) => s.clear);
  const { data, isLoading, isError, error, refetch } = useMe();

  const signedOut = isError && isDefinitiveAuthFailure(error);
  const unreachable = isError && !signedOut;

  // Drop the persisted snapshot only when the server actually says we are not
  // signed in — otherwise GuestGuard would send us straight back here.
  useEffect(() => {
    if (storedUser && signedOut) clear();
  }, [storedUser, signedOut, clear]);

  if (signedOut) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // A stored user plus an unreachable server: keep the app up. The session is
  // probably fine and react-query is already retrying behind this.
  if (storedUser) return <>{children}</>;

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  // No stored user and the server cannot be reached — say so, and offer a way
  // out that is not "your login was wrong", because it was not.
  if (unreachable) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="font-medium">Cannot reach the server</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          Your session is still valid. The API did not answer — check that it is running, then try
          again.
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!data) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export function GuestGuard({ children }: { children: ReactNode }) {
  const user = useAuth((s) => s.user);
  const roles = useAuth((s) => s.roles);
  // Already signed in — bounce to the role-appropriate landing page
  // (salesperson → pipeline, employee → self-service, everyone else →
  // dashboard). This keeps a demo Employee who reloads /login from
  // landing back on /dashboard and staring at KPIs they can't see.
  if (user) return <Navigate to={homePathForRoles(roles)} replace />;
  return <>{children}</>;
}
